import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, CardHeader,
  Select, MenuItem, FormControl, InputLabel, Chip, CircularProgress,
  Alert, Divider, Tooltip, IconButton, ToggleButtonGroup, ToggleButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  FormControlLabel, Switch,
} from '@mui/material';
import { Refresh as RefreshIcon, InfoOutlined, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useQuery, useQueryClient } from 'react-query';
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line,
  XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, Legend, ReferenceLine,
} from 'recharts';
import { metricsAPI } from '../services/api';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useAuth } from '../contexts/AuthContext';
import PsychologyPanel from '../components/PsychologyPanel';
import { MASTER_PERF_SUB_TABS } from '../constants/viewerPermissions';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt2 = (v) =>
  v == null ? '-' : new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

const fmt4 = (v) =>
  v == null ? '-' : new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(v);

const fmtPct = (v) => (v == null ? '-' : `${fmt4(v)}%`);

const fmtDuration = (seconds) => {
  if (seconds == null || seconds === 0) return '-';
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h < 24) return `${h}h ${m}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
};

const fmtTime = (unixSec) => {
  if (!unixSec) return '-';
  return new Date(unixSec * 1000).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' });
};

const fmtDate = (unixSec) => {
  if (!unixSec) return '-';
  return new Date(unixSec * 1000).toLocaleDateString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' });
};

// ─── Metric Row ───────────────────────────────────────────────────────────────

function MetricRow({ label, value, sub, color, tooltip }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', py: 0.55, px: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
          {label}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} placement="top" arrow enterTouchDelay={0}>
            <InfoOutlined sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help', flexShrink: 0 }} />
          </Tooltip>
        )}
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', color: color || 'text.primary' }}>
          {value}
        </Typography>
        {sub && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>
            {sub}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ─── Chart helpers ────────────────────────────────────────────────────────────

const chartTickStyle = { fill: '#94a3b8', fontSize: 10 };

function fmtChartDate(unixMs) {
  const d = new Date(unixMs);
  return `${d.getDate()}/${d.getMonth() + 1} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

function CustomTooltipBase({ active, payload, label, rows }) {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{ bgcolor: '#1e293b', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 1, p: 1.5, fontSize: '0.75rem' }}>
      <Typography sx={{ color: '#94a3b8', fontSize: '0.7rem', mb: 0.5 }}>{fmtChartDate(label)}</Typography>
      {rows.map((r, i) => (
        payload[i] != null && (
          <Box key={r.key} sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
            <span style={{ color: r.color }}>{r.label}</span>
            <strong style={{ color: r.color }}>{r.format ? r.format(payload[i]?.value) : payload[i]?.value}</strong>
          </Box>
        )
      ))}
    </Box>
  );
}

const SNAP_WINDOWS = [
  { label: '50 điểm', value: 50 },
  { label: '200 điểm', value: 200 },
  { label: 'Tất cả', value: 0 },
];

function MetricCharts({ accountId, fromDate, toDate, periodLabel }) {
  const [window, setWindow] = useState(200);

  const { data, isLoading } = useQuery(
    ['metrics-snapshots', accountId, fromDate, toDate],
    () => metricsAPI.getSnapshots(accountId, {
      ...(fromDate ? { from: fromDate } : {}),
      ...(toDate   ? { to:   toDate   } : {}),
    }),
    { enabled: !!accountId, refetchInterval: 30000 }
  );

  const allSnaps = data?.data?.data || [];
  const snaps = window === 0 ? allSnaps : allSnaps.slice(-window);

  if (isLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
  );
  if (snaps.length < 2) return (
    <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
      Chưa đủ dữ liệu lịch sử{periodLabel ? ` trong kỳ "${periodLabel}"` : ''} để vẽ biểu đồ. Cần ít nhất 2 lần EA cập nhật trong khoảng thời gian này.
    </Alert>
  );

  const chartData = snaps.map(s => ({
    t: s.snapshot_at * 1000,
    net_profit: +s.net_profit.toFixed(2),
    equity: +s.current_equity.toFixed(2),
    floating: +s.floating_pl.toFixed(2),
    win_rate: +s.win_rate.toFixed(2),
    pf: +(Math.min(s.profit_factor, 20)).toFixed(3),
    dd: +(-Math.abs(s.max_closed_dd_pct)).toFixed(3),
    cur_dd: +(-Math.abs(s.current_closed_dd_pct)).toFixed(3),
    deals: s.total_closed_deals,
  }));

  const pColor = (v) => (v >= 0 ? '#4ade80' : '#f87171');
  const latestProfit = chartData[chartData.length - 1]?.net_profit ?? 0;
  const latestEquity = chartData[chartData.length - 1]?.equity ?? 0;

  const axisProps = { tick: chartTickStyle, axisLine: false, tickLine: false };
  const gridProps = { stroke: 'rgba(255,255,255,0.05)', strokeDasharray: '3 3' };

  return (
    <Box sx={{ mb: 1 }}>
      {/* Window selector */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <ToggleButtonGroup
          size="small"
          value={window}
          exclusive
          onChange={(_, v) => { if (v !== null) setWindow(v); }}
          sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1.5, fontSize: '0.7rem', borderColor: 'rgba(255,255,255,0.12)', color: 'text.secondary' }, '& .Mui-selected': { color: '#60a5fa !important', bgcolor: '#3b82f620 !important' } }}
        >
          {SNAP_WINDOWS.map(w => <ToggleButton key={w.value} value={w.value}>{w.label}</ToggleButton>)}
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={2}>

        {/* Net Profit Curve */}
        <Grid item xs={12} md={6}>
          <ChartCard title="Net Profit (All-time)" color="#3b82f6"
            badge={<span style={{ color: pColor(latestProfit), fontWeight: 700 }}>${fmt2(latestProfit)}</span>}
          >
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gpProfit" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={pColor(latestProfit)} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={pColor(latestProfit)} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="t" tickFormatter={fmtChartDate} {...axisProps} interval="preserveStartEnd" />
                <YAxis {...axisProps} tickFormatter={v => `$${v >= 1000 ? (v/1000).toFixed(1)+'k' : v}`} width={55} />
                <ReferenceLine y={0} stroke="rgba(255,255,255,0.15)" />
                <RTooltip content={<CustomTooltipBase rows={[{ key:'net_profit', label:'Net Profit', color: pColor(latestProfit), format: v => `$${fmt2(v)}` }]} />} />
                <Area type="monotone" dataKey="net_profit" stroke={pColor(latestProfit)} strokeWidth={1.5} fill="url(#gpProfit)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Equity */}
        <Grid item xs={12} md={6}>
          <ChartCard title="Current Equity" color="#10b981"
            badge={<span style={{ color: '#4ade80', fontWeight: 700 }}>${fmt2(latestEquity)}</span>}
          >
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gpEquity" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="t" tickFormatter={fmtChartDate} {...axisProps} interval="preserveStartEnd" />
                <YAxis {...axisProps} tickFormatter={v => `$${v >= 1000 ? (v/1000).toFixed(1)+'k' : v}`} width={55} />
                <RTooltip content={<CustomTooltipBase rows={[
                  { key:'equity', label:'Equity', color:'#4ade80', format: v => `$${fmt2(v)}` },
                  { key:'floating', label:'Floating', color:'#fbbf24', format: v => `$${fmt2(v)}` },
                ]} />} />
                <Area type="monotone" dataKey="equity" stroke="#10b981" strokeWidth={1.5} fill="url(#gpEquity)" dot={false} />
                <Line type="monotone" dataKey="floating" stroke="#fbbf24" strokeWidth={1} dot={false} strokeDasharray="4 2" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Win Rate + Profit Factor */}
        <Grid item xs={12} md={6}>
          <ChartCard title="Win Rate & Profit Factor" color="#8b5cf6">
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="t" tickFormatter={fmtChartDate} {...axisProps} interval="preserveStartEnd" />
                <YAxis yAxisId="wr" {...axisProps} tickFormatter={v => `${v}%`} domain={[0,100]} width={42} />
                <YAxis yAxisId="pf" orientation="right" {...axisProps} tickFormatter={v => v} domain={[0,'auto']} width={32} />
                <ReferenceLine yAxisId="wr" y={50} stroke="rgba(255,255,255,0.12)" strokeDasharray="3 3" />
                <ReferenceLine yAxisId="pf" y={1} stroke="rgba(251,191,36,0.3)" strokeDasharray="3 3" />
                <RTooltip content={<CustomTooltipBase rows={[
                  { key:'win_rate', label:'Win Rate', color:'#a78bfa', format: v => `${fmt2(v)}%` },
                  { key:'pf', label:'PF', color:'#fbbf24', format: v => fmt4(v) },
                ]} />} />
                <Legend wrapperStyle={{ fontSize: '0.7rem', color: '#94a3b8', paddingTop: 4 }} />
                <Line yAxisId="wr" type="monotone" dataKey="win_rate" name="Win Rate %" stroke="#a78bfa" strokeWidth={1.5} dot={false} />
                <Line yAxisId="pf" type="monotone" dataKey="pf" name="Profit Factor" stroke="#fbbf24" strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Drawdown */}
        <Grid item xs={12} md={6}>
          <ChartCard title="Max Closed Drawdown %" color="#f59e0b">
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gpDd" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f87171" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#f87171" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="t" tickFormatter={fmtChartDate} {...axisProps} interval="preserveStartEnd" />
                <YAxis {...axisProps} tickFormatter={v => `${v}%`} width={42} />
                <ReferenceLine y={0} stroke="rgba(255,255,255,0.15)" />
                <RTooltip content={<CustomTooltipBase rows={[
                  { key:'dd', label:'Max DD', color:'#f87171', format: v => `${fmt2(Math.abs(v))}%` },
                  { key:'cur_dd', label:'Cur DD', color:'#fbbf24', format: v => `${fmt2(Math.abs(v))}%` },
                ]} />} />
                <Area type="monotone" dataKey="dd" stroke="#f87171" strokeWidth={1.5} fill="url(#gpDd)" dot={false} />
                <Line type="monotone" dataKey="cur_dd" stroke="#fbbf24" strokeWidth={1} dot={false} strokeDasharray="4 2" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

      </Grid>
    </Box>
  );
}

// ─── Chart Card ──────────────────────────────────────────────────────────────

function ChartCard({ title, children, color = '#3b82f6', badge }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'rgba(255,255,255,0.08)' }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{title}</span>
            {badge && <span>{badge}</span>}
          </Box>
        }
        titleTypographyProps={{ variant: 'subtitle2', fontWeight: 700, fontSize: '0.78rem', letterSpacing: 0.5, textTransform: 'uppercase', component: 'div' }}
        sx={{
          py: 0.75, px: 1.5,
          background: `linear-gradient(90deg, ${color}22 0%, transparent 100%)`,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      />
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        {children}
      </CardContent>
    </Card>
  );
}

// ─── Section Card ─────────────────────────────────────────────────────────────

function SectionCard({ title, children, color = '#3b82f6' }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'rgba(255,255,255,0.08)' }}>
      <CardHeader
        title={title}
        titleTypographyProps={{ variant: 'subtitle2', fontWeight: 700, fontSize: '0.8rem', letterSpacing: 0.5, textTransform: 'uppercase' }}
        sx={{
          py: 1, px: 1.5,
          background: `linear-gradient(90deg, ${color}22 0%, transparent 100%)`,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      />
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        {children}
      </CardContent>
    </Card>
  );
}

// ─── Account Metrics Panel ────────────────────────────────────────────────────

function AccountMetricsPanel({ m }) {
  if (!m) return null;

  const pColor = (v) => (v > 0 ? '#4ade80' : v < 0 ? '#f87171' : 'text.primary');
  const ddColor = (v) => (v > 0 ? '#f87171' : 'text.primary');

  return (
    <Grid container spacing={2}>
      {/* Balance & Cashflow */}
      <Grid item xs={12} sm={6} lg={4}>
        <SectionCard title="Balance & Cashflow" color="#3b82f6">
          <MetricRow label="Balance đầu kỳ" value={`$${fmt2(m.starting_balance)}`}
            tooltip="Số dư tài khoản tại thời điểm bắt đầu kỳ thống kê đang chọn" />
          <Divider sx={{ opacity: 0.1 }} />
          <MetricRow label="Balance cuối kỳ" value={`$${fmt2(m.ending_balance)}`}
            tooltip="Số dư tài khoản hiện tại (cuối kỳ), chưa tính lãi/lỗ của vị thế đang mở" />
          <Divider sx={{ opacity: 0.1 }} />
          <MetricRow label="Tổng nạp" value={`+$${fmt2(m.total_deposits)}`} color="#4ade80"
            tooltip="Tổng tiền nạp vào tài khoản trong kỳ" />
          <Divider sx={{ opacity: 0.1 }} />
          <MetricRow label="Tổng rút" value={`-$${fmt2(m.total_withdrawals)}`} color="#f87171"
            tooltip="Tổng tiền rút khỏi tài khoản trong kỳ" />
          <Divider sx={{ opacity: 0.1 }} />
          <MetricRow label="Nạp ròng" value={`$${fmt2(m.net_deposits)}`} color={pColor(m.net_deposits)}
            tooltip="Nạp trừ rút trong kỳ — dùng để tách lợi nhuận thật khỏi dòng tiền nạp/rút" />
        </SectionCard>
      </Grid>

      {/* Trade Performance */}
      <Grid item xs={12} sm={6} lg={4}>
        <SectionCard title="Hiệu suất giao dịch" color="#8b5cf6">
          <MetricRow
            label="Net Profit"
            value={`$${fmt2(m.net_profit)}`}
            sub={fmtPct(m.total_return_pct)}
            color={pColor(m.net_profit)}
            tooltip="Lợi nhuận ròng đã chốt trong kỳ (không tính nạp/rút). % bên dưới = lợi nhuận so với balance đầu kỳ"
          />
          <Divider sx={{ opacity: 0.1 }} />
          <MetricRow label="Gross Profit / Loss"
            value={`+$${fmt2(m.gross_profit)} / -$${fmt2(m.gross_loss)}`}
            tooltip="Tổng lãi của tất cả lệnh thắng / tổng lỗ của tất cả lệnh thua trong kỳ"
          />
          <Divider sx={{ opacity: 0.1 }} />
          <MetricRow
            label="Win Rate"
            value={fmtPct(m.win_rate)}
            sub={`${m.winning_deals}W / ${m.losing_deals}L${m.breakeven_deals > 0 ? ` / ${m.breakeven_deals}BE` : ''} (${m.total_closed_deals} vị thế)`}
            color={m.win_rate >= 50 ? '#4ade80' : '#f87171'}
            tooltip="Tỷ lệ lệnh thắng trên tổng vị thế đã đóng. W = thắng, L = thua, BE = hòa vốn"
          />
          <Divider sx={{ opacity: 0.1 }} />
          <MetricRow
            label="Profit Factor"
            value={m.profit_factor >= 999 ? '∞' : fmt4(m.profit_factor)}
            color={m.profit_factor >= 1.5 ? '#4ade80' : m.profit_factor >= 1 ? '#fbbf24' : '#f87171'}
            tooltip="Gross Profit ÷ Gross Loss — kiếm được bao nhiêu $ trên mỗi 1$ thua. >1 = có lãi, ≥1.5 = tốt, <1 = đang lỗ"
          />
          <Divider sx={{ opacity: 0.1 }} />
          <MetricRow label="Payoff Ratio (Avg Win/Loss)"
            value={m.payoff_ratio >= 999 ? '∞' : fmt4(m.payoff_ratio)}
            sub={`Avg Win: $${fmt2(m.avg_win)} / Avg Loss: $${fmt2(m.avg_loss)}`}
            tooltip="Lãi trung bình 1 lệnh thắng ÷ lỗ trung bình 1 lệnh thua. >1 = lệnh thắng ăn dày hơn lệnh thua mất"
          />
          <Divider sx={{ opacity: 0.1 }} />
          <MetricRow
            label="Expectancy / Deal"
            value={`$${fmt2(m.expectancy_per_deal)}`}
            color={pColor(m.expectancy_per_deal)}
            tooltip="Kỳ vọng lãi trung bình mỗi lệnh = Net Profit ÷ số vị thế đã đóng. Dương = hệ thống có lợi thế"
          />
        </SectionCard>
      </Grid>

      {/* Daily & Holding */}
      <Grid item xs={12} sm={6} lg={4}>
        <SectionCard title="Thống kê thời gian" color="#06b6d4">
          <MetricRow label="Avg Daily P/L" value={`$${fmt2(m.avg_daily_pl)}`} color={pColor(m.avg_daily_pl)}
            tooltip="Lãi/lỗ trung bình mỗi ngày có giao dịch trong kỳ" />
          <Divider sx={{ opacity: 0.1 }} />
          <MetricRow label="Số ngày giao dịch" value={`${m.trading_days} ngày`}
            tooltip="Số ngày có ít nhất 1 lệnh được đóng trong kỳ" />
          <Divider sx={{ opacity: 0.1 }} />
          <MetricRow label="Avg Holding Time" value={fmtDuration(m.avg_holding_time)}
            tooltip="Thời gian giữ lệnh trung bình từ lúc mở đến lúc đóng" />
          <Divider sx={{ opacity: 0.1 }} />
          <MetricRow
            label="R² (Equity Curve)"
            value={fmt4(m.r_squared)}
            color={m.r_squared >= 0.9 ? '#4ade80' : m.r_squared >= 0.7 ? '#fbbf24' : '#f87171'}
            tooltip="Độ đều của đường vốn (0→1). Càng gần 1 = tăng trưởng đều đặn, ổn định; thấp = lên xuống thất thường"
          />
          <Divider sx={{ opacity: 0.1 }} />
          <MetricRow
            label="Kỳ"
            value={m.from_date && m.to_date ? `${fmtDate(m.from_date)} → ${fmtDate(m.to_date)}` : '-'}
            tooltip="Khoảng thời gian dữ liệu đang được thống kê (theo kỳ chọn ở trên)"
          />
        </SectionCard>
      </Grid>

      {/* Closed Drawdown */}
      <Grid item xs={12} sm={6} lg={4}>
        <SectionCard title="Closed Drawdown" color="#f59e0b">
          <MetricRow
            label="Max Closed DD"
            value={`$${fmt2(m.max_closed_dd_abs)}`}
            sub={fmtPct(m.max_closed_dd_pct)}
            color={ddColor(m.max_closed_dd_abs)}
            tooltip="Drawdown sâu nhất của balance (chỉ tính lệnh đã đóng): mức sụt lớn nhất từ đỉnh vốn xuống đáy trong kỳ"
          />
          <Divider sx={{ opacity: 0.1 }} />
          <MetricRow
            label="Current Closed DD"
            value={m.in_closed_drawdown ? `$${fmt2(m.current_closed_dd_abs)}` : 'Không có'}
            sub={m.in_closed_drawdown ? fmtPct(m.current_closed_dd_pct) : undefined}
            color={m.in_closed_drawdown ? '#f87171' : '#4ade80'}
            tooltip="Mức sụt giảm hiện tại của balance so với đỉnh vốn gần nhất. 'Không có' = đang ở đỉnh"
          />
          <Divider sx={{ opacity: 0.1 }} />
          <MetricRow
            label="Đang trong DD?"
            value={
              <Chip
                label={m.in_closed_drawdown ? 'Có' : 'Không'}
                size="small"
                sx={{
                  height: 18, fontSize: '0.7rem',
                  bgcolor: m.in_closed_drawdown ? '#ef444433' : '#22c55e33',
                  color: m.in_closed_drawdown ? '#f87171' : '#4ade80',
                }}
              />
            }
          />
          <Divider sx={{ opacity: 0.1 }} />
          <MetricRow label="Max Recovery Time" value={fmtDuration(m.max_closed_recovery_time)}
            tooltip="Thời gian LÂU NHẤT từng cần để vốn quay lại đỉnh cũ sau một đợt sụt giảm" />
          <Divider sx={{ opacity: 0.1 }} />
          <MetricRow label="Avg Recovery Time" value={fmtDuration(m.avg_closed_recovery_time)}
            tooltip="Thời gian TRUNG BÌNH để vốn quay lại đỉnh cũ sau mỗi đợt sụt giảm" />
          <Divider sx={{ opacity: 0.1 }} />
          <MetricRow label="Current Recovery Duration" value={m.in_closed_drawdown ? fmtDuration(m.current_closed_recovery_dur) : '-'} color={m.in_closed_drawdown ? '#fbbf24' : undefined}
            tooltip="Đợt drawdown hiện tại đã kéo dài bao lâu (tính từ lúc rời đỉnh vốn). '-' = không trong drawdown" />
          <Divider sx={{ opacity: 0.1 }} />
          <MetricRow label="Số lần đã recover" value={`${m.closed_recovery_count} lần`}
            tooltip="Số lần vốn đã sụt rồi vượt lại đỉnh cũ thành công trong kỳ" />
        </SectionCard>
      </Grid>

      {/* Equity (Real-time) card removed 2026-08-22 — realtime data lives on the Dashboard tab */}

      {/* Equity DD Runtime */}
      <Grid item xs={12} sm={6} lg={4}>
        <SectionCard title="Equity DD (Runtime từ lúc EA attach)" color="#6366f1">
          <MetricRow
            label="Max Equity DD"
            value={`$${fmt2(m.max_equity_dd_abs)}`}
            sub={fmtPct(m.max_equity_dd_pct)}
            color={ddColor(m.max_equity_dd_abs)}
            tooltip="Drawdown equity sâu nhất đo realtime (tính cả floating) kể từ lúc EA được attach"
          />
          <Divider sx={{ opacity: 0.1 }} />
          <MetricRow label="Max Equity Recovery" value={fmtDuration(m.max_equity_recovery_time)}
            tooltip="Thời gian lâu nhất để equity quay lại đỉnh sau một đợt sụt" />
          <Divider sx={{ opacity: 0.1 }} />
          <MetricRow label="Avg Equity Recovery" value={fmtDuration(m.avg_equity_recovery_time)}
            tooltip="Thời gian trung bình để equity quay lại đỉnh sau mỗi đợt sụt" />
          <Divider sx={{ opacity: 0.1 }} />
          <MetricRow label="Số lần recover" value={`${m.equity_recovery_count} lần`}
            tooltip="Số lần equity sụt rồi vượt lại đỉnh cũ (đo từ lúc EA chạy)" />
          <Divider sx={{ opacity: 0.1 }} />
          <MetricRow
            label="Đang Equity DD?"
            value={
              <Chip
                label={m.in_equity_drawdown ? 'Có' : 'Không'}
                size="small"
                sx={{
                  height: 18, fontSize: '0.7rem',
                  bgcolor: m.in_equity_drawdown ? '#ef444433' : '#22c55e33',
                  color: m.in_equity_drawdown ? '#f87171' : '#4ade80',
                }}
              />
            }
          />
          <Divider sx={{ opacity: 0.1 }} />
          <MetricRow
            label="Current DD Duration"
            value={m.in_equity_drawdown ? fmtDuration(m.current_equity_recovery_dur) : '-'}
            color={m.in_equity_drawdown ? '#fbbf24' : undefined}
          />
        </SectionCard>
      </Grid>
    </Grid>
  );
}

// ─── Period config ────────────────────────────────────────────────────────────

const PERIODS = [
  { key: 'today',        label: 'Hôm nay' },
  { key: 'yesterday',    label: 'Hôm qua' },
  { key: 'this_week',    label: 'Tuần này' },
  { key: 'last_week',    label: 'Tuần trước' },
  { key: 'this_month',   label: 'Tháng này' },
  { key: 'last_month',   label: 'Tháng trước' },
  { key: 'last_7_days',  label: '7 ngày' },
  { key: 'last_30_days', label: '30 ngày' },
  { key: 'last_90_days', label: '90 ngày' },
  { key: 'ytd',          label: 'YTD' },
  { key: 'all',          label: 'Tất cả' },
];

// ─── Main Page ────────────────────────────────────────────────────────────────

// ─── V4.5 Quản trị vốn: Kỷ luật giao dịch (vi phạm + lịch ngày) ────────────────

function DisciplineCalendar({ accountId }) {
  const { data: statsData } = useQuery(
    ['daily-stats', accountId],
    () => metricsAPI.getDailyStats({ account_id: accountId }).then(res => res.data),
    { refetchInterval: 60000 }
  );
  const { data: violData } = useQuery(
    ['violations', accountId],
    () => metricsAPI.getViolations({ account_id: accountId }).then(res => res.data),
    { refetchInterval: 60000 }
  );

  // Chế độ xem: 'calendar' (mặc định) | 'table' — persist qua localStorage
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('discipline_view_mode') === 'table' ? 'table' : 'calendar'; } catch { return 'calendar'; }
  });
  useEffect(() => {
    try { localStorage.setItem('discipline_view_mode', viewMode); } catch {}
  }, [viewMode]);

  // Bộ lọc lý do vi phạm (dropdown, mặc định 'all') — persist localStorage
  const [violFilter, setViolFilter] = useState(() => {
    try { return localStorage.getItem('discipline_viol_filter') || 'all'; } catch { return 'all'; }
  });
  useEffect(() => {
    try { localStorage.setItem('discipline_viol_filter', violFilter); } catch {}
  }, [violFilter]);

  // Tháng đang xem trên lịch
  const [viewYM, setViewYM] = useState(() => {
    const now = new Date();
    return { y: now.getFullYear(), m: now.getMonth() + 1 };
  });
  const shiftMonth = (delta) => {
    setViewYM(({ y, m }) => {
      const d = new Date(y, m - 1 + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() + 1 };
    });
  };

  const allRows = statsData?.data || [];
  const rows = allRows.slice(0, 31);   // dạng bảng: ~1 tháng gần nhất
  const allViolations = violData?.data || [];
  // Lý do vi phạm (DisciplineViolations.vtype — Master EA ghi vào CopyTrade_Violations_*.csv)
  const VIOL_REASONS = {
    NO_SL_FLOAT:  'Không SL, lỗ tạm tính vượt ngưỡng',
    CLOSED_LOSS:  'Đóng lệnh lỗ vượt ngưỡng',
    SL_SLIPPAGE:  'Chạm SL trượt giá (lỗ vượt ngưỡng do trượt)',
    WRONG_VOLUME: 'Sai volume quy định',
  };
  const reasonLabel = (t) => VIOL_REASONS[t] || t || 'Không rõ';
  // Danh sách lý do cho dropdown: lý do đã biết + lý do lạ xuất hiện trong dữ liệu
  const reasonOptions = [...new Set([...Object.keys(VIOL_REASONS), ...allViolations.map(v => v.vtype).filter(Boolean)])];
  const violations = violFilter === 'all' ? allViolations : allViolations.filter(v => v.vtype === violFilter);
  const totalViolations = violations.length;
  // Gom theo lý do: toàn kỳ + theo ngày (vdate yyyy.mm.dd)
  const countByReason = (list) => {
    const m = {};
    for (const v of list) m[v.vtype || '?'] = (m[v.vtype || '?'] || 0) + 1;
    return m;
  };
  const reasonSummary = (list) => Object.entries(countByReason(list))
    .sort((a, b) => b[1] - a[1])
    .map(([t, n]) => `${n} ${reasonLabel(t)}`).join(' · ');
  const totalByReason = countByReason(allViolations);
  const violByDate = {};
  for (const v of violations) {
    if (!v.vdate) continue;
    (violByDate[v.vdate] = violByDate[v.vdate] || []).push(v);
  }
  // Số vi phạm hiển thị cho 1 ngày: lọc → đếm từ danh sách; 'all' → ưu tiên counter EA (r.violations)
  const dayViolCount = (r) => {
    const list = violByDate[r?.stat_date] || [];
    if (violFilter !== 'all') return list.length;
    return r?.violations != null ? r.violations : list.length;
  };
  const dayViolTooltip = (r) => {
    const list = violByDate[r?.stat_date] || [];
    const n = dayViolCount(r);
    if (!n) return '';
    return `${n} vi phạm${list.length ? ': ' + reasonSummary(list) : ''}`;
  };
  const todayRow = allRows.length > 0 && allRows[0].backfilled !== 1 ? allRows[0] : null;

  if (allRows.length === 0 && totalViolations === 0) {
    return (
      <Alert severity="info" sx={{ mt: 3 }}>
        Chưa có dữ liệu kỷ luật. Hãy chạy EA <strong>Copy_Master_V4_5</strong> để thu thập
        số lệnh buy/sell, SL hit và vi phạm nguyên tắc theo ngày.
      </Alert>
    );
  }

  const pnlColor = (v) => (v == null ? 'text.secondary' : v >= 0 ? '#4ade80' : '#f87171');

  // Khóa giao dịch do Master chạm lỗ ngày.
  //   locked     = snapshot hiện tại (về 0 sau giờ mở khóa)
  //   was_locked = DÍNH theo ngày (đã khóa bất kỳ lúc nào trong ngày)
  //   locked_at  = unix lần đầu thấy khóa, lock_until = unix giờ mở khóa
  const fmtHM = (unix) => (unix ? new Date(unix * 1000).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' }) : null);
  const lockInfo = (r) => {
    if (!r) return null;
    const now = Number(r.locked) === 1;
    const was = now || Number(r.was_locked) === 1;
    if (!was) return null;
    const from = fmtHM(r.locked_at);
    const until = fmtHM(r.lock_until);
    const range = from || until ? ` (${from ? `từ ${from}` : ''}${from && until ? ' ' : ''}${until ? `đến ${until}` : ''})` : '';
    return {
      now,
      short: now ? 'Đang khóa' : 'Đã khóa',
      reason: 'chạm lỗ ngày',
      text: `${now ? '🔒 ĐANG KHÓA' : '🔒 Đã khóa trong ngày'} — Master chạm lỗ tối đa ngày${range}`,
    };
  };

  // ── Dữ liệu cho lưới lịch tháng ──
  const monthPrefix = `${viewYM.y}.${String(viewYM.m).padStart(2, '0')}.`;
  const statsByDay = {};
  for (const r of allRows) {
    if (r.stat_date && r.stat_date.startsWith(monthPrefix)) {
      statsByDay[parseInt(r.stat_date.slice(-2), 10)] = r;
    }
  }
  const daysInMonth = new Date(viewYM.y, viewYM.m, 0).getDate();
  const firstOffset = (new Date(viewYM.y, viewYM.m - 1, 1).getDay() + 6) % 7; // Thứ 2 = cột đầu
  const nowD = new Date();
  const todayNum = (nowD.getFullYear() === viewYM.y && nowD.getMonth() + 1 === viewYM.m) ? nowD.getDate() : -1;
  const cells = [];
  for (let i = 0; i < firstOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Kỷ luật giao dịch — vi phạm nguyên tắc &amp; thống kê ngày
        </Typography>
        <ToggleButtonGroup
          size="small"
          value={viewMode}
          exclusive
          onChange={(_, v) => { if (v !== null) setViewMode(v); }}
          sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1.5, fontSize: '0.72rem' } }}
        >
          <ToggleButton value="calendar">📅 Lịch</ToggleButton>
          <ToggleButton value="table">☰ Bảng</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Summary chips */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Tooltip title={allViolations.length ? `Theo lý do: ${reasonSummary(allViolations)}` : ''}>
          <Chip
            label={`Tổng vi phạm nguyên tắc: ${totalViolations}${violFilter !== 'all' ? ` / ${allViolations.length}` : ''}`}
            size="small"
            sx={{ fontWeight: 700, bgcolor: totalViolations > 0 ? '#ef444433' : '#22c55e22', color: totalViolations > 0 ? '#f87171' : '#4ade80' }}
          />
        </Tooltip>
        {/* Thống kê theo lý do — bấm chip để lọc nhanh, bấm lại để bỏ lọc */}
        {Object.entries(totalByReason).sort((a, b) => b[1] - a[1]).map(([t, n]) => (
          <Chip
            key={t}
            label={`${reasonLabel(t)}: ${n}`}
            size="small"
            variant={violFilter === t ? 'filled' : 'outlined'}
            onClick={() => setViolFilter(violFilter === t ? 'all' : t)}
            sx={{ fontSize: '0.7rem', height: 22, fontWeight: 600,
                  color: violFilter === t ? '#fff' : '#fca5a5',
                  bgcolor: violFilter === t ? '#ef4444' : 'transparent',
                  borderColor: '#ef444488', cursor: 'pointer' }}
          />
        ))}
        <FormControl size="small" sx={{ minWidth: 220, ml: 'auto' }}>
          <InputLabel id="viol-filter-label">Lọc lý do vi phạm</InputLabel>
          <Select
            labelId="viol-filter-label"
            label="Lọc lý do vi phạm"
            value={violFilter}
            onChange={(e) => setViolFilter(e.target.value)}
            sx={{ fontSize: '0.8rem' }}
          >
            <MenuItem value="all">Tất cả lý do ({allViolations.length})</MenuItem>
            {reasonOptions.map(t => (
              <MenuItem key={t} value={t}>{reasonLabel(t)} ({totalByReason[t] || 0})</MenuItem>
            ))}
          </Select>
        </FormControl>
        {todayRow && (
          <>
            <Chip label={`Hôm nay: ${todayRow.buy_count ?? 0} Buy / ${todayRow.sell_count ?? 0} Sell`}
              size="small" sx={{ fontWeight: 700, bgcolor: '#3b82f622', color: '#60a5fa' }} />
            <Chip label={`SL hit hôm nay: ${todayRow.sl_full_hits ?? 0}`}
              size="small" sx={{ fontWeight: 700, bgcolor: (todayRow.sl_full_hits || 0) > 0 ? '#f59e0b33' : '#22c55e22', color: (todayRow.sl_full_hits || 0) > 0 ? '#fbbf24' : '#4ade80' }} />
            {Number(todayRow.locked) === 1 ? (
              <Tooltip title={lockInfo(todayRow)?.text || ''}>
                <Chip label="🔒 ĐANG KHÓA GIAO DỊCH (chạm lỗ ngày)" size="small"
                  sx={{ fontWeight: 700, bgcolor: '#ef444433', color: '#f87171' }} />
              </Tooltip>
            ) : Number(todayRow.was_locked) === 1 ? (
              <Tooltip title={lockInfo(todayRow)?.text || ''}>
                <Chip label="🔒 Hôm nay đã khóa (chạm lỗ ngày) — đã mở" size="small"
                  sx={{ fontWeight: 700, bgcolor: '#f59e0b33', color: '#fbbf24' }} />
              </Tooltip>
            ) : null}
          </>
        )}
      </Box>

      {/* ── Chế độ LỊCH (mặc định) ── */}
      {viewMode === 'calendar' && (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
            <IconButton size="small" onClick={() => shiftMonth(-1)}><ChevronLeft fontSize="small" /></IconButton>
            <Typography sx={{ fontWeight: 700, minWidth: 130, textAlign: 'center', fontSize: '0.9rem' }}>
              Tháng {viewYM.m}/{viewYM.y}
            </Typography>
            <IconButton size="small" onClick={() => shiftMonth(1)}><ChevronRight fontSize="small" /></IconButton>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.5 }}>
            {['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'].map(d => (
              <Typography key={d} align="center" variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                {d}
              </Typography>
            ))}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
            {cells.map((d, idx) => {
              if (d === null) return <Box key={`e${idx}`} />;
              const r = statsByDay[d];
              const pnl = r ? r.daily_pnl : null;
              const bg = !r ? 'transparent'
                : pnl > 0 ? 'rgba(34,197,94,0.14)'
                : pnl < 0 ? 'rgba(239,68,68,0.14)'
                : 'rgba(148,163,184,0.08)';
              const isToday = d === todayNum;
              const lk = lockInfo(r);
              return (
                <Tooltip
                  key={d}
                  title={r ? `${r.stat_date}${r.backfilled === 1 ? ' — backfill từ lịch sử deal, chỉ có lãi/lỗ' : ''}${lk ? ` — ${lk.text}` : ''}${dayViolTooltip(r) ? ` — ⚠ ${dayViolTooltip(r)}` : ''}` : ''}
                  disableHoverListener={!r}
                >
                  <Box sx={{
                    minHeight: 88, borderRadius: 1, p: 0.75,
                    border: '1px solid',
                    borderColor: isToday ? '#3b82f6' : 'rgba(255,255,255,0.08)',
                    bgcolor: bg,
                    display: 'flex', flexDirection: 'column', gap: 0.4,
                    cursor: r ? 'default' : 'auto',
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: isToday ? '#60a5fa' : 'text.secondary', fontWeight: isToday ? 700 : 400, lineHeight: 1, fontSize: '0.78rem' }}>
                        {d}
                      </Typography>
                      {lk && <span style={{ fontSize: '0.75rem', lineHeight: 1, opacity: lk.now ? 1 : 0.75 }}>🔒</span>}
                    </Box>
                    {r && (
                      <>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: pnlColor(pnl), lineHeight: 1.2, fontSize: '0.85rem' }}>
                          {pnl == null ? '—' : `${pnl >= 0 ? '+' : ''}${fmt2(pnl)}$`}
                        </Typography>
                        {r.buy_count != null && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', lineHeight: 1.2, fontWeight: 600 }}>
                            <span style={{ color: '#4ade80' }}>{r.buy_count}▲</span>
                            {' '}
                            <span style={{ color: '#f87171' }}>{r.sell_count}▼</span>
                            {(r.sl_full_hits || 0) > 0 ? <span> · SL {r.sl_full_hits}</span> : null}
                          </Typography>
                        )}
                        {dayViolCount(r) > 0 && (
                          <Typography variant="caption" sx={{ color: '#f87171', fontSize: '0.73rem', fontWeight: 700, lineHeight: 1.2 }}>
                            ⚠ {dayViolCount(r)} vi phạm{violFilter !== 'all' ? ' (lọc)' : ''}
                          </Typography>
                        )}
                        {lk && (
                          <Typography variant="caption" sx={{ color: lk.now ? '#f87171' : '#fbbf24', fontSize: '0.7rem', fontWeight: 700, lineHeight: 1.2 }}>
                            🔒 {lk.short}: {lk.reason}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Paper>
      )}

      {/* ── Chế độ BẢNG ── */}
      {viewMode === 'table' && (
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Ngày</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Lãi/Lỗ ($)</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Buy</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Sell</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>SL hit</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Vi phạm</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Khóa</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.stat_date} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {r.stat_date}
                  {r.backfilled === 1 && (
                    <Tooltip title="Backfill từ lịch sử deal (trước khi chạy V4.5) — chỉ có lãi/lỗ">
                      <Chip label="cũ" size="small" sx={{ ml: 0.5, height: 16, fontSize: '0.6rem' }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: pnlColor(r.daily_pnl) }}>
                  {r.daily_pnl == null ? '—' : `${r.daily_pnl >= 0 ? '+' : ''}${fmt2(r.daily_pnl)}`}
                </TableCell>
                <TableCell align="center">{r.buy_count ?? '—'}</TableCell>
                <TableCell align="center">{r.sell_count ?? '—'}</TableCell>
                <TableCell align="center" sx={{ color: (r.sl_full_hits || 0) > 0 ? '#fbbf24' : 'inherit', fontWeight: (r.sl_full_hits || 0) > 0 ? 700 : 400 }}>
                  {r.sl_full_hits ?? '—'}
                </TableCell>
                <TableCell align="center" sx={{ color: dayViolCount(r) > 0 ? '#f87171' : 'inherit', fontWeight: dayViolCount(r) > 0 ? 700 : 400 }}>
                  <Tooltip title={dayViolTooltip(r)}>
                    <span style={{ cursor: dayViolCount(r) > 0 ? 'help' : 'default' }}>
                      {r.backfilled === 1 && violFilter === 'all' ? '—' : dayViolCount(r)}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                  {(() => {
                    const lk = lockInfo(r);
                    if (!lk) return '';
                    return (
                      <Tooltip title={lk.text}>
                        <Chip
                          label={`🔒 ${lk.short}: ${lk.reason}`}
                          size="small"
                          sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700,
                                bgcolor: lk.now ? '#ef444433' : '#f59e0b33',
                                color: lk.now ? '#f87171' : '#fbbf24' }}
                        />
                      </Tooltip>
                    );
                  })()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}
    </Box>
  );
}

export default function MasterPerformance() {
  const [selectedAccount, setSelectedAccount] = useState('');
  const [periodKey, setPeriodKey] = useState('all');
  const { canSeeMasterPerfSubTab, canAccountSeeMasterPerfSubTab } = useAuth();
  // Persist active sub-tab across visits, like Reports.js does for `reports_tab_key`.
  const [subTab, setSubTab] = useState(() => {
    try {
      const saved = localStorage.getItem('master_perf_sub_tab');
      if (saved && MASTER_PERF_SUB_TABS.find(t => t.key === saved)) return saved;
    } catch {}
    return 'overview';
  });
  useEffect(() => {
    try { localStorage.setItem('master_perf_sub_tab', subTab); } catch {}
  }, [subTab]);

  const visibleSubTabs = MASTER_PERF_SUB_TABS.filter(t => canSeeMasterPerfSubTab(t.key));
  // If saved tab is no longer visible, fall back to first visible
  useEffect(() => {
    if (visibleSubTabs.length > 0 && !visibleSubTabs.find(t => t.key === subTab)) {
      setSubTab(visibleSubTabs[0].key);
    }
  }, [visibleSubTabs, subTab]);

  const queryClient = useQueryClient();
  const { lastMessage } = useWebSocket();

  // V4.5 UI: mặc định ẩn tài khoản đã xóa / đang ngừng — toggle để xem lịch sử
  const [showInactive, setShowInactive] = useState(() => {
    try { return localStorage.getItem('master_perf_show_inactive') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('master_perf_show_inactive', showInactive ? '1' : '0'); } catch {}
  }, [showInactive]);
  const inactiveParam = showInactive ? { include_inactive: 1 } : {};

  const { data: allData } = useQuery(
    ['metrics-all-accounts', periodKey, showInactive],
    () => metricsAPI.getAll({ period_key: periodKey, ...inactiveParam }),
    { refetchInterval: 60000 }
  );

  const { data, isLoading, isError, refetch } = useQuery(
    ['metrics', selectedAccount, periodKey, showInactive],
    () => metricsAPI.getAll({
      ...(selectedAccount ? { account_id: selectedAccount } : {}),
      period_key: periodKey,
      ...inactiveParam,
    }),
    { refetchInterval: 60000 }
  );

  // Refresh on WebSocket metrics update
  useEffect(() => {
    if (lastMessage?.type === 'mt5_metrics') {
      queryClient.invalidateQueries({ queryKey: ['metrics'] });
      queryClient.invalidateQueries({ queryKey: ['metrics-all-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['metrics-snapshots'] });
    }
  }, [lastMessage, queryClient]);

  const allMasters = allData?.data?.data || [];
  const metrics = data?.data?.data || [];

  // For viewers, filter the account dropdown to those allowed by the current sub-tab.
  const dropdownMasters = allMasters.filter(m =>
    canAccountSeeMasterPerfSubTab(m.mt5_account_id, subTab)
  );
  // Reset selection if the previously selected account is no longer visible after sub-tab change
  useEffect(() => {
    if (selectedAccount && !dropdownMasters.find(m => m.mt5_account_id === selectedAccount)) {
      setSelectedAccount('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTab]);

  const displayedMetrics = (selectedAccount
    ? metrics.filter(m => m.mt5_account_id === selectedAccount)
    : metrics
  ).filter(m => canAccountSeeMasterPerfSubTab(m.mt5_account_id, subTab));

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Hiệu suất Master
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Các chỉ số hiệu suất giao dịch tính toán bởi EA Account_Metrics
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Tooltip title="Mặc định chỉ hiện tài khoản đang hoạt động. Bật để xem lại lịch sử các tài khoản đã xóa hoặc đang ngừng chạy.">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
              }
              label={<Typography variant="caption" sx={{ color: 'text.secondary' }}>Hiện TK đã xóa/ngừng</Typography>}
              sx={{ mr: 0.5 }}
            />
          </Tooltip>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Master Account</InputLabel>
            <Select
              value={selectedAccount}
              label="Master Account"
              onChange={e => setSelectedAccount(e.target.value)}
            >
              <MenuItem value="">Tất cả ({dropdownMasters.length})</MenuItem>
              {dropdownMasters.map(m => (
                <MenuItem key={m.mt5_account_id} value={m.mt5_account_id}>
                  {m.account_name ? `${m.account_name} (${m.mt5_account_id})` : m.mt5_account_id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Làm mới">
            <IconButton onClick={() => refetch()} size="small">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Sub-tab selector */}
      {visibleSubTabs.length > 1 && (
        <Box sx={{ mb: 2 }}>
          <ToggleButtonGroup
            size="small"
            value={subTab}
            exclusive
            onChange={(_, v) => { if (v !== null) setSubTab(v); }}
            sx={{
              '& .MuiToggleButton-root': { py: 0.5, px: 2, fontSize: '0.8rem', borderColor: 'rgba(255,255,255,0.12)', color: 'text.secondary' },
              '& .Mui-selected': { color: '#60a5fa !important', bgcolor: '#3b82f620 !important' },
            }}
          >
            {visibleSubTabs.map(t => (
              <ToggleButton key={t.key} value={t.key}>{t.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Period selector — only meaningful for Overview tab */}
      {subTab === 'overview' && (
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 3 }}>
        {PERIODS.map(p => (
          <Box
            key={p.key}
            onClick={() => setPeriodKey(p.key)}
            sx={{
              px: 1.5, py: 0.5,
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.78rem',
              fontWeight: 600,
              border: '1px solid',
              transition: 'all 0.15s',
              borderColor: periodKey === p.key ? '#3b82f6' : 'rgba(255,255,255,0.12)',
              bgcolor: periodKey === p.key ? '#3b82f620' : 'transparent',
              color: periodKey === p.key ? '#60a5fa' : 'text.secondary',
              '&:hover': {
                borderColor: '#3b82f680',
                color: '#93c5fd',
              },
            }}
          >
            {p.label}
          </Box>
        ))}
      </Box>
      )}

      {/* ─── Overview tab (existing content) ─────────────────────────────── */}
      {subTab === 'overview' && (
      <>
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Không thể tải dữ liệu hiệu suất.
        </Alert>
      )}

      {!isLoading && !isError && displayedMetrics.length === 0 && (
        <Alert severity="info">
          Chưa có dữ liệu. Hãy attach EA <strong>Account_Metrics_1.2</strong> lên chart tài khoản master với{' '}
          <strong>InpDashboardExport = true</strong>.
        </Alert>
      )}

      {displayedMetrics.map((m) => (
        <Box key={m.mt5_account_id} sx={{ mb: 4 }}>
          {/* Account header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {m.account_name || m.mt5_account_id}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip label={m.mt5_account_id} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                {m.group_name && (
                  <Chip label={m.group_name} size="small" sx={{ fontSize: '0.7rem', height: 20, bgcolor: '#3b82f622', color: '#60a5fa' }} />
                )}
                <Chip
                  label={PERIODS.find(p => p.key === periodKey)?.label || periodKey}
                  size="small"
                  sx={{ fontSize: '0.7rem', height: 20, bgcolor: '#8b5cf622', color: '#a78bfa' }}
                />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Cập nhật: {m.ea_updated_at ? fmtTime(m.ea_updated_at) : (m.last_updated ? new Date(m.last_updated).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' }) : '-')}
                </Typography>
              </Box>
            </Box>

            {/* Quick summary chips */}
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Chip
                label={`Net ${m.net_profit >= 0 ? '+' : ''}$${fmt2(m.net_profit)}`}
                size="small"
                sx={{ fontWeight: 700, bgcolor: m.net_profit >= 0 ? '#22c55e33' : '#ef444433', color: m.net_profit >= 0 ? '#4ade80' : '#f87171' }}
              />
              <Chip
                label={`WR ${fmtPct(m.win_rate)}`}
                size="small"
                sx={{ fontWeight: 700, bgcolor: m.win_rate >= 50 ? '#22c55e22' : '#ef444422', color: m.win_rate >= 50 ? '#4ade80' : '#f87171' }}
              />
              <Chip
                label={`PF ${m.profit_factor >= 999 ? '∞' : fmt4(m.profit_factor)}`}
                size="small"
                sx={{ fontWeight: 700, bgcolor: m.profit_factor >= 1 ? '#3b82f622' : '#ef444422', color: m.profit_factor >= 1 ? '#60a5fa' : '#f87171' }}
              />
            </Box>
          </Box>

          <AccountMetricsPanel m={m} />

          {/* Charts — below metrics, filtered to selected period */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Biểu đồ lịch sử — {PERIODS.find(p => p.key === periodKey)?.label || periodKey}
            </Typography>
            <MetricCharts
              accountId={m.mt5_account_id}
              fromDate={m.from_date || null}
              toDate={m.to_date || null}
              periodLabel={PERIODS.find(p => p.key === periodKey)?.label}
            />
          </Box>

          {/* V4.5: Kỷ luật giao dịch — vi phạm + bảng lịch ngày */}
          <DisciplineCalendar accountId={m.mt5_account_id} />

          <Divider sx={{ mt: 3, opacity: 0.15 }} />
        </Box>
      ))}
      </>
      )}

      {/* ─── Psychology (Đánh giá cảm xúc) tab ─────────────────────────── */}
      {subTab === 'psychology' && (
        <Box>
          {dropdownMasters.length === 0 && (
            <Alert severity="info">
              Chưa có dữ liệu cảm xúc nào. Hãy attach EA <strong>Trader_Spychology_Monitor</strong> lên chart tài khoản master.
            </Alert>
          )}
          {(selectedAccount
            ? dropdownMasters.filter(m => m.mt5_account_id === selectedAccount)
            : dropdownMasters
          ).map(m => (
            <Box key={m.mt5_account_id} sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    {m.account_name || m.mt5_account_id}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                    <Chip label={m.mt5_account_id} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                    {m.group_name && (
                      <Chip label={m.group_name} size="small" sx={{ fontSize: '0.7rem', height: 20, bgcolor: '#3b82f622', color: '#60a5fa' }} />
                    )}
                  </Box>
                </Box>
              </Box>
              <PsychologyPanel accountId={m.mt5_account_id} />
              <Divider sx={{ mt: 3, opacity: 0.15 }} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
